import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { monEspaceAPI } from '../api/monEspace.api';
import { PrivateAppointment } from '../types/monEspace.types';

interface UsePrivateAppointmentsParams {
  startDate?: string;
  endDate?: string;
}

/**
 * Hook for managing private appointments
 * Uses React Query for caching and optimistic updates
 */
export const usePrivateAppointments = (params?: UsePrivateAppointmentsParams) => {
  const queryClient = useQueryClient();

  // Query key includes date range for proper cache management
  const queryKey = ['monEspace', 'appointments', params?.startDate, params?.endDate];

  // Fetch appointments
  const {
    data: appointments = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: () => monEspaceAPI.getAppointments(params)
  });

  // Create appointment mutation
  const createMutation = useMutation({
    mutationFn: (appointment: Partial<PrivateAppointment>) =>
      monEspaceAPI.createAppointment(appointment),
    onSuccess: () => {
      // Invalidate and refetch appointments
      queryClient.invalidateQueries({ queryKey: ['monEspace', 'appointments'] });
    }
  });

  // Update appointment mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<PrivateAppointment> }) =>
      monEspaceAPI.updateAppointment(id, updates),
    onSuccess: () => {
      // Invalidate and refetch appointments
      queryClient.invalidateQueries({ queryKey: ['monEspace', 'appointments'] });
    }
  });

  // Delete appointment mutation
  const deleteMutation = useMutation({
    mutationFn: (appointmentId: string) =>
      monEspaceAPI.deleteAppointment(appointmentId),
    onSuccess: () => {
      // Invalidate and refetch appointments
      queryClient.invalidateQueries({ queryKey: ['monEspace', 'appointments'] });
    }
  });

  // Wrapper functions for easier usage
  const createAppointment = async (appointment: Partial<PrivateAppointment>) => {
    return createMutation.mutateAsync(appointment);
  };

  const updateAppointment = async (id: string, updates: Partial<PrivateAppointment>) => {
    return updateMutation.mutateAsync({ id, updates });
  };

  const deleteAppointment = async (id: string) => {
    return deleteMutation.mutateAsync(id);
  };

  return {
    // Data
    appointments,

    // Loading states
    isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Error states
    error,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    // Mutations
    createAppointment,
    updateAppointment,
    deleteAppointment,

    // Refetch
    refetch
  };
};
